import { useEventListener, usePreferredReducedMotion, useScroll, useWindowSize } from "@vueuse/core";
import { ref, toRef, watch } from "vue";
// Returns null when `time` falls in a gap or outside the transcript.
const findActive = (words, time) => {
    if (words.length === 0)
        return null;
    let low = 0;
    let high = words.length - 1;
    while (low <= high) {
        const mid = (low + high) >>> 1;
        const word = words[mid];
        if (time < word.start)
            high = mid - 1;
        else if (time >= word.end)
            low = mid + 1;
        else
            return word;
    }
    return null;
};
/** Scrolls the active transcript word into view during playback. Suspends on any manual document scroll, resumes on the next play. */
export const useTranscriptAutoScroll = (options) => {
    const { currentTime, isPlaying, flatWords, wordElements, seekTick, onWordUnrendered } = options;
    const userSuspended = ref(false);
    let lastActiveId = null;
    // Set just before our own scrollIntoView so the document scroll listener
    // doesn't read the auto-scroll as a manual one.
    let programmaticScroll = false;
    const prefersReducedMotion = usePreferredReducedMotion();
    const { height: windowHeight } = useWindowSize();
    const suspend = () => {
        userSuspended.value = true;
    };
    // Consumer-invoked when the user explicitly re-engages (click a word, scrub).
    // Clears suspension so the next active-word change scrolls back into view.
    const resume = () => {
        userSuspended.value = false;
    };
    // Wheel/touch are instant intent signals (fire before the scroll, and at a
    // scroll boundary where no scroll event is emitted).
    useEventListener(window, ["wheel", "touchstart"], suspend, { passive: true });
    // Scrollbar-drag and keyboard scrolling emit only `scroll`. onStop re-arms the
    // guard once our own programmatic scroll has settled.
    useScroll(window, {
        onScroll: () => {
            if (!programmaticScroll)
                suspend();
        },
        onStop: () => {
            programmaticScroll = false;
        }
    });
    watch([currentTime, flatWords], () => {
        const active = findActive(flatWords.value, currentTime.value);
        const activeId = active?.id ?? null;
        if (activeId === lastActiveId)
            return;
        lastActiveId = activeId;
        if (userSuspended.value || active == null)
            return;
        const el = wordElements.value.get(active.id);
        if (!el) {
            // Clear the dedupe so the next tick re-resolves once the reveal has mounted the word.
            lastActiveId = null;
            onWordUnrendered?.(active.id);
            return;
        }
        const { top, bottom } = el.getBoundingClientRect();
        if (top >= 0 && bottom <= windowHeight.value)
            return;
        programmaticScroll = true;
        el.scrollIntoView({
            block: "center",
            behavior: prefersReducedMotion.value === "reduce" ? "instant" : "smooth"
        });
    }, { immediate: true });
    watch(isPlaying, (now, prev) => {
        if (now && !prev)
            userSuspended.value = false;
    });
    // Scrubbing the player progress bar must re-engage scrolling, even when
    // paused — `seeked` only fires on the scrub-end, so a monotonic tick is
    // simpler than wiring through the player's internal isSeeking flag.
    if (seekTick !== undefined) {
        watch(toRef(seekTick), resume);
    }
    return { resume };
};
